import React, { Fragment, useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Alert, Card, Flex, Space, Spin } from 'antd'
import { useFetchDistributionBetweenPhasesAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { useFetchQuestionsQuery } from '../../../../../../../store/apis/question.api'
import { getColorByIndex } from '../../../../../../../utils/colors.helper'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import PatternIcon from '../../../../../../../components/common/PatternIcon'
import { buildAssignmentPath } from '../../../../../../../utils/link.helper'
import Phases from '../../../../../../../constants/model-phases'
import { useVersionId } from '../../../../../../../utils/parseURL.helper'
import { useNavigate } from 'react-router-dom'
import { flatten } from '../../../../../../../utils/array.helper'

const BAR_HEIGHT = 64

const DistributionBetweenPhasesChart = ({ token, phasesToShow = Object.values(Phases) }) => {
    const navigate = useNavigate()
    const versionId = useVersionId()

    const {
        data: analytics,
        isFetching: isFetchingAnalytics,
        error,
    } = useFetchDistributionBetweenPhasesAnalyticsQuery({ token, phasesToShow })

    let { data: questions, isFetching: isFetchingQuestions } = useFetchQuestionsQuery({ token, versionId })
    questions = flatten(questions, { excludeParents: true })

    const isFetching = isFetchingAnalytics || isFetchingQuestions

    const sortedCategoriesLargeToSmall = useMemo(() => {
        if (!analytics?.questions?.length) return []

        const categorySubmissionsMap = new Map()

        analytics.questions.forEach(question => {
            const phase = question.phases.find(p => p.name === phasesToShow[0])
            if (phase) {
                phase.categories
                    .filter(cat => cat.id !== null)
                    .forEach(cat => {
                        const prevCount = categorySubmissionsMap.get(cat.id) || 0
                        categorySubmissionsMap.set(cat.id, prevCount + cat.numberOfSubmissions)
                    })
            }
        })

        return Array.from(categorySubmissionsMap.entries())
            .sort((a, b) => b[1] - a[1]) // Sort by total numberOfSubmissions descending
            .map(([catId]) => catId)
    }, [analytics, phasesToShow])

    const allCategories = useMemo(() => {
        const categoriesMap = new Map()
        questions?.forEach(question => {
            question.categories?.forEach(category => {
                categoriesMap.set(category.id, category)
            })
        })
        return Array.from(categoriesMap.values()).sort((a, b) => {
            const aIndex = sortedCategoriesLargeToSmall.indexOf(a.id)
            const bIndex = sortedCategoriesLargeToSmall.indexOf(b.id)
            if (aIndex === -1) return 1
            if (bIndex === -1) return -1
            return aIndex - bIndex
        })
    }, [questions, sortedCategoriesLargeToSmall])

    const data = React.useMemo(() => {
        if (!analytics || !questions) return []

        return questions
            .map(question => {
                const analyticsQuestion = analytics.questions.find(q => q.id === question.id)
                if (!analyticsQuestion) return null

                const result = {
                    name: question.name,
                    questionId: question.id,
                    isHighDivergent: analyticsQuestion.isHighDivergent,
                }

                phasesToShow.forEach(phaseName => {
                    const phase = analyticsQuestion.phases.find(p => p.name === phaseName)
                    if (!phase) return

                    phase.categories.forEach(category => {
                        const categoryDetails = question.categories?.find(c => c.id === category.id)
                        if (!categoryDetails) return
                        const dataKey = `${phaseName}_${category.id}`
                        result[dataKey] = category.percentFromReviewedSubmissions
                        result[`${dataKey}-count`] = category.numberOfSubmissions
                        result[`${dataKey}-color`] = getColorByIndex(categoryDetails.colorId).value
                        result[`${dataKey}-name`] = categoryDetails.name
                        result[`${dataKey}-phase`] = phaseName
                    })
                })

                return result
            })
            .filter(Boolean)
    }, [analytics, questions, phasesToShow])

    const chartHeight = Math.max(250, data.length * BAR_HEIGHT + 100)

    const handleBarClick = data => {
        if (data && data.questionId) navigate(buildAssignmentPath(token, data.questionId))
    }

    const renderCustomYAxisTick = React.useCallback(
        props => {
            const { x, y, payload } = props
            const questionData = data.find(d => d.name === payload.value)
            const isHighDivergent = questionData?.isHighDivergent

            return (
                <g transform={`translate(${x},${y})`}>
                    <text
                        x={0}
                        y={0}
                        dy={0}
                        textAnchor='end'
                        fill={isHighDivergent ? 'red' : '#666'}
                        style={{ fontSize: 12 }}
                    >
                        {payload.value.length > 20 ? `${payload.value.substring(0, 20)}...` : payload.value}
                        {isHighDivergent && <title>This question has high divergence</title>}
                    </text>
                </g>
            )
        },
        [data],
    )

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null

        const question = questions.find(question => question.name === label)

        const groupedByPhase = payload.reduce((acc, entry) => {
            const [phaseName] = entry.name.split('_')
            if (!acc[phaseName]) acc[phaseName] = []

            const questionData = data.find(d => d.name === label)
            const categoryName = questionData[`${entry.name}-name`]
            const count = questionData[`${entry.name}-count`]

            acc[phaseName].push({
                categoryName,
                value: entry.value,
                count,
                color: entry.color,
            })

            return acc
        }, {})

        return (
            <Card 
                size='small' 
                className='min-w-[300px]'
                style={{ 
                    position: 'fixed', 
                    maxHeight: '300px',
                    overflowY: 'auto',
                    pointerEvents: 'auto',
                    right: '20px',
                    top: '200px'
                }}
                onMouseMove={(e) => {
                    // Prevent the tooltip from following the mouse when inside the card
                    e.stopPropagation();
                }}
            >
                <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '0.9rem' }}>
                    {question.name}
                </Title>
                <Flex vertical gap={8}>
                    {Object.entries(groupedByPhase).map(([phaseName, items], phaseIndex) => (
                        <Fragment key={phaseName}>
                            <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                                {phaseName}:
                            </Title>
                            <Flex vertical gap={8}>
                                {items.map((item, itemIndex) => (
                                    <Flex key={`${phaseIndex}-${itemIndex}`} justify='space-between' align='center' gap={8}>
                                        <Space size={4} style={{ maxWidth: 320 }}>
                                            <PatternIcon
                                                color={item.color}
                                                id={item.categoryName}
                                                showId={false}
                                                sizeInPx={16}
                                            />
                                            <Text
                                                style={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    //fontSize: '0.8rem',
                                                }}
                                            >
                                                {item.categoryName}
                                            </Text>
                                        </Space>
                                        {`${item.value}% - ${item.count} submissions`}
                                    </Flex>
                                ))}
                            </Flex>
                        </Fragment>
                    ))}
                </Flex>
            </Card>
        )
    }

    if (error) return <Alert message={error} type='error' showIcon />

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart data={data} layout='vertical' margin={{ top: 0, right: 12, left: 12, bottom: 36 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis
                        type='number'
                        tickFormatter={value => `${value}%`}
                        label={{
                            value: 'Percent of Submissions',
                            position: 'bottom',
                            offset: 15,
                        }}
                        domain={[0, 100]}
                    />
                    <YAxis
                        dataKey='name'
                        type='category'
                        tick={renderCustomYAxisTick}
                        width={144}
                        label={{
                            value: 'Questions',
                            angle: -90,
                            position: 'left',
                        }}
                    />
                    <Tooltip 
                        content={<CustomTooltip />} 
                        wrapperStyle={{ pointerEvents: 'none' }}
                    />
                    {phasesToShow.map(phaseName =>
                        allCategories.map(category => (
                            <Bar
                                key={`${phaseName}-${category.id}`}
                                dataKey={`${phaseName}_${category.id}`}
                                stackId={phaseName}
                                fill={getColorByIndex(category.colorId).value}
                                name={`${phaseName}_${category.id}`}
                                onClick={handleBarClick}
                                style={{ cursor: 'pointer' }}
                            />
                        )),
                    )}
                </BarChart>
            </ResponsiveContainer>
        </Spin>
    )
}

export default DistributionBetweenPhasesChart
